/**
 * 分类组合配菜：
 * 硬菜 1 + 点心 1 + 水果 1 + 主食 1
 * + 时令蔬菜 1～2 道
 * + 剩余售价额度用普通菜填满（总售价不得高于总预算）
 * + 若 needWei：再加 1 种煨菜
 *
 * 预估利润率 = (总售价 − 总成本) / 总成本
 * 有目标利润率：不得低于目标
 * 无目标利润率：默认不得低于 50%
 * 无利润率上限
 */
import { REQUIRED_SINGLE_CATEGORIES } from '@/constants/categories'

const EPS = 1e-8
const MAX_ATTEMPTS = 60

function shuffle(list) {
  const arr = list.slice()
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = arr[i]
    arr[i] = arr[j]
    arr[j] = tmp
  }
  return arr
}

function byCategory(dishes, category) {
  return (dishes || []).filter((d) => d.category === category)
}

function toLine(dish, guestCount) {
  const n = Number(guestCount) || 1
  return {
    ...dish,
    qty: n,
    linePrice: Number(dish.price) * n,
    lineCost: Number(dish.cost) * n
  }
}

function usedCost(lines) {
  return lines.reduce((sum, line) => sum + Number(line.lineCost), 0)
}

function usedPrice(lines) {
  return lines.reduce((sum, line) => sum + Number(line.linePrice), 0)
}

function markupOf(dish) {
  const cost = Number(dish.cost)
  if (cost <= 0) return Number(dish.price) > 0 ? Infinity : 0
  return (Number(dish.price) - cost) / cost
}

function rateOf(price, cost) {
  if (cost <= 0) return null
  return (price - cost) / cost
}

function selectedIds(lines) {
  return new Set(lines.map((d) => d.id))
}

function meetsMinRate(rate, minRate) {
  return rate != null && rate + EPS >= minRate
}

function canAddDish(selected, dish, context) {
  const line = toLine(dish, context.guestCount)
  const nextPrice = usedPrice(selected) + line.linePrice
  if (nextPrice > Number(context.maxPrice) + EPS) {
    return false
  }
  const minRate = Number(context.minRate)
  const nextCost = usedCost(selected) + line.lineCost
  const nextRate = rateOf(nextPrice, nextCost)
  const currentRate = rateOf(usedPrice(selected), usedCost(selected))
  if (nextRate == null) return false
  if (meetsMinRate(currentRate, minRate)) {
    return nextRate + EPS >= minRate
  }
  return nextRate > (currentRate == null ? -Infinity : currentRate)
}

function tryFill(dishes, context) {
  const guestCount = context.guestCount
  const warnings = []
  const selected = []

  REQUIRED_SINGLE_CATEGORIES.forEach((category) => {
    const pool = byCategory(dishes, category)
    if (!pool.length) {
      const names = { hard: '硬菜', dessert: '点心', fruit: '水果', staple: '主食' }
      throw new Error(`缺少「${names[category]}」，请先到菜谱管理录入该类菜品`)
    }
    selected.push(toLine(pickOne(pool, context.preferHighMarkup), guestCount))
  })

  if (context.needWei) {
    const weiPool = byCategory(dishes, 'wei')
    if (!weiPool.length) {
      throw new Error('已选择需要煨菜，但菜谱中没有煨菜，请先录入')
    }
    selected.push(toLine(pickOne(weiPool, context.preferHighMarkup), guestCount))
  }

  const seasonalPool = shuffle(byCategory(dishes, 'seasonal'))
  let seasonalCount = 0
  seasonalPool.forEach((dish) => {
    if (seasonalCount >= 2) return
    if (canAddDish(selected, dish, context)) {
      selected.push(toLine(dish, guestCount))
      seasonalCount += 1
    }
  })
  if (!seasonalPool.length) {
    warnings.push('菜谱中没有时令蔬菜，已跳过')
  } else if (seasonalCount === 0) {
    warnings.push('剩余售价额度不足以加入时令蔬菜，已跳过')
  }

  const byMarkup = (a, b) => markupOf(b) - markupOf(a)

  const normals = byCategory(dishes, 'normal').slice().sort(byMarkup)
  normals.forEach((dish) => {
    if (canAddDish(selected, dish, context)) {
      selected.push(toLine(dish, guestCount))
    }
  })

  const extra = (dishes || [])
    .filter((d) => d.category === 'normal' || d.category === 'seasonal')
    .filter((d) => !selectedIds(selected).has(d.id))
    .slice()
    .sort(byMarkup)
  extra.forEach((dish) => {
    if (canAddDish(selected, dish, context)) {
      selected.push(toLine(dish, guestCount))
    }
  })

  const totalPrice = usedPrice(selected)
  const overCap = totalPrice > Number(context.maxPrice) + EPS
  if (overCap) {
    warnings.push('必选菜（含煨菜）售价已超过总预算')
  }

  return {
    dishes: selected,
    warnings,
    overCap,
    totalPrice,
    totalCost: usedCost(selected)
  }
}

function pickOne(pool, preferHighMarkup) {
  if (!pool.length) return null
  if (preferHighMarkup) {
    const sorted = pool.slice().sort((a, b) => markupOf(b) - markupOf(a))
    const top = sorted.slice(0, Math.min(3, sorted.length))
    return shuffle(top)[0]
  }
  return shuffle(pool)[0]
}

function meetsConstraints(result, context) {
  if (result.totalPrice > Number(context.maxPrice) + EPS) return false
  const estimated = rateOf(result.totalPrice, result.totalCost)
  return meetsMinRate(estimated, Number(context.minRate))
}

/**
 * @param {Array} dishes
 * @param {{ maxPrice: number, guestCount: number, needWei: boolean, minRate: number }} context
 */
export function selectDishes(dishes, context) {
  const guestCount = Number(context.guestCount) || 1
  const ctxBase = {
    ...context,
    guestCount,
    needWei: Boolean(context.needWei),
    minRate: Number(context.minRate),
    maxPrice: Number(context.maxPrice != null ? context.maxPrice : context.totalBudget)
  }

  REQUIRED_SINGLE_CATEGORIES.forEach((category) => {
    if (!byCategory(dishes, category).length) {
      const names = { hard: '硬菜', dessert: '点心', fruit: '水果', staple: '主食' }
      throw new Error(`缺少「${names[category]}」，请先到菜谱管理录入该类菜品`)
    }
  })
  if (ctxBase.needWei && !byCategory(dishes, 'wei').length) {
    throw new Error('已选择需要煨菜，但菜谱中没有煨菜，请先录入')
  }

  for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
    const ctx = { ...ctxBase, preferHighMarkup: i % 2 === 0 }
    const result = tryFill(dishes, ctx)
    if (meetsConstraints(result, ctx)) {
      return result
    }
  }

  throw new Error('无法配出总售价不超过总预算且预估利润率达标的菜单，请调整餐标、目标利润率或菜品价格')
}
